/**
 * Share of the bar interval passed since the open of the last bar; wall
 * clock against the bar times, like the countdown (overlays/countdown.js).
 *
 * @param {{ lastBarTime: number, barDurationMs: number, now: number }} time - UTC ms
 * @returns {number} ≥ 0
 */
const phaseAt = ({ lastBarTime, barDurationMs, now }) =>
  Math.max(0, (now - lastBarTime) / barDurationMs);

/**
 * The view follows real time: while it follows, the bars move left evenly
 * by one bar spacing per bar interval (TimeScale phase), so a new bar lands
 * in the room that has opened for it. Starting and stopping keep every bar
 * where it is: the phase moves into the right offset and back.
 *
 * @param {object} timeScale
 */
export const createViewFollow = (timeScale) => {
  let isFollowing = false;

  const setRightOffset = (rightOffset) => {
    timeScale.setView({ barSpacing: timeScale.barSpacing, rightOffset });
  };

  /** Stops following; the bars stay where they are. */
  const freeze = () => {
    if (!isFollowing) return;

    setRightOffset(timeScale.rightOffset + timeScale.phase);
    timeScale.setPhase(0);
    isFollowing = false;
  };

  return {
    get isFollowing() {
      return isFollowing;
    },

    /**
     * Every frame: follows or freezes the view.
     *
     * @param {{ shouldFollow: boolean, lastBarTime: number, barDurationMs: number, now: number }} params -
     *   `shouldFollow`: animation on, autoscroll on, the last bar visible; times in UTC ms
     */
    update: ({ shouldFollow, ...time }) => {
      if (!shouldFollow) {
        freeze();
        return;
      }

      const phase = phaseAt(time);

      if (!isFollowing) {
        setRightOffset(timeScale.rightOffset - phase);
        isFollowing = true;
      }

      timeScale.setPhase(phase);
    },

    /**
     * A bar was added while following, before TimeScale.appendBar: sets the
     * phase of the new bar.
     *
     * @param {{ lastBarTime: number, barDurationMs: number, now: number }} time - of the new bar, UTC ms
     * @returns {number} bars the view still has to slide: ≈ 0 when the clock agrees with the bar times
     */
    appendBar: (time) => {
      const previousPhase = timeScale.phase;
      const phase = phaseAt(time);

      timeScale.setPhase(phase);

      return 1 + phase - previousPhase;
    },

    freeze,
  };
};
